// useSessions.ts
// ================
// Custom hook for managing sessions with Firebase CRUD operations
// Feature #28 - Implements Firebase CRUD for sessions

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  createSession,
  getSessions,
  getSessionsByTopic,
  getSession,
  updateSession,
  deleteSession,
  SessionData,
  SessionStatus,
  ConfidenceChecks,
} from '../services/firebase';
import type { TeachingForm } from '../types';
import i18n from '../i18n';

// Re-export types for convenience
export type { SessionData, SessionStatus, ConfidenceChecks };

// Type representing raw session data from Firestore with Timestamp objects
// This is the format we receive from Firebase before normalization
interface FirebaseTimestamp {
  toDate(): Date;
}

interface RawSessionData {
  id?: string;
  userId: string;
  learningJourneyId?: string;
  topicId: string;
  topicTitle?: string;
  subtopicIds: string[];
  teachingForm: TeachingForm;
  startedAt: FirebaseTimestamp | Date | string;
  endedAt?: FirebaseTimestamp | Date | string | null;
  durationMinutes?: number;
  goals?: string[];
  summary?: string | null;
  keyTakeaways?: string[];
  discussionPrompts?: string[];
  confidenceChecks?: ConfidenceChecks;
  status: SessionStatus;
  createdAt?: FirebaseTimestamp | Date | string;
}

// Helper to convert Firebase Timestamp to Date/string for consistent handling
function normalizeSessionDates(session: RawSessionData): SessionData {
  const normalized = { ...session } as SessionData;

  // Handle startedAt conversion
  if (session.startedAt) {
    if (typeof session.startedAt === 'object' && 'toDate' in session.startedAt) {
      // Firebase Timestamp
      normalized.startedAt = session.startedAt.toDate().toISOString();
    } else if (session.startedAt instanceof Date) {
      normalized.startedAt = session.startedAt.toISOString();
    }
  }

  // Handle endedAt conversion
  if (session.endedAt) {
    if (typeof session.endedAt === 'object' && 'toDate' in session.endedAt) {
      normalized.endedAt = session.endedAt.toDate().toISOString();
    } else if (session.endedAt instanceof Date) {
      normalized.endedAt = session.endedAt.toISOString();
    }
  }

  // Handle createdAt conversion (if present)
  // Note: createdAt is not part of SessionData interface but may exist in raw Firestore data
  if (session.createdAt) {
    const createdAtValue = session.createdAt;
    if (typeof createdAtValue === 'object' && 'toDate' in createdAtValue) {
      (normalized as SessionData & { createdAt?: string }).createdAt =
        (createdAtValue as FirebaseTimestamp).toDate().toISOString();
    } else if (createdAtValue instanceof Date) {
      (normalized as SessionData & { createdAt?: string }).createdAt =
        createdAtValue.toISOString();
    }
  }

  return normalized;
}

export interface UseSessionsResult {
  // Data
  sessions: SessionData[];
  loading: boolean;
  error: string | null;

  // Actions
  fetchSessions: () => Promise<void>;
  fetchSessionsByTopic: (topicId: string) => Promise<SessionData[]>;
  createNewSession: (data: CreateSessionInput) => Promise<{ success: boolean; id?: string; error?: string }>;
  updateSessionData: (sessionId: string, data: UpdateSessionInput) => Promise<{ success: boolean; error?: string }>;
  endSession: (sessionId: string, summary?: string, keyTakeaways?: string[]) => Promise<{ success: boolean; error?: string }>;
  deleteSessionById: (sessionId: string) => Promise<{ success: boolean; error?: string }>;
  getSessionById: (sessionId: string) => Promise<SessionData | null>;

  // Utilities
  getSessionFromCache: (sessionId: string) => SessionData | undefined;
  getRecentSessions: (limit?: number) => SessionData[];
  getSessionsByForm: (form: TeachingForm) => SessionData[];
  getUnfinishedSessions: () => SessionData[]; // Feature #249: Get active/unfinished sessions
}

export interface CreateSessionInput {
  topicId: string;
  topicTitle?: string;
  subtopicIds?: string[];
  teachingForm: TeachingForm;
  learningJourneyId?: string;
  goals?: string[];
}

export interface UpdateSessionInput {
  endedAt?: Date | string | null;
  durationMinutes?: number;
  summary?: string | null;
  keyTakeaways?: string[];
  confidenceChecks?: ConfidenceChecks;
  status?: SessionStatus;
  goals?: string[];
}

export function useSessions(): UseSessionsResult {
  const { user, isMockAuth } = useAuth();
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // LocalStorage key for mock auth fallback
  const localStorageKey = user?.uid ? `lyra_sessions_${user.uid}` : 'lyra_sessions';

  // Fetch all sessions for the current user
  const fetchSessions = useCallback(async () => {
    if (!user?.uid) {
      setSessions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isMockAuth) {
        // Use localStorage for mock auth (development)
        const saved = localStorage.getItem(localStorageKey);
        const parsed = saved ? JSON.parse(saved) : [];
        setSessions(parsed);
      } else {
        // Use Firebase for real auth
        const result = await getSessions(user.uid);
        if (result.success && result.data) {
          // Normalize dates from Firebase Timestamps
          const normalized = result.data.map((s: RawSessionData) => normalizeSessionDates(s));
          setSessions(normalized);
        } else {
          setError(result.error || 'Kunne ikke hente samtaler');
          setSessions([]);
        }
      }
    } catch (err) {
      console.error('[useSessions] Error fetching sessions:', err);
      setError(i18n.t('errors:fetch.sessions'));
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, isMockAuth, localStorageKey]);

  // Fetch sessions for a specific topic
  const fetchSessionsByTopic = useCallback(async (topicId: string): Promise<SessionData[]> => {
    if (!user?.uid) {
      return [];
    }

    try {
      if (isMockAuth) {
        // Use localStorage for mock auth (development)
        const saved = localStorage.getItem(localStorageKey);
        const parsed: SessionData[] = saved ? JSON.parse(saved) : [];
        return parsed.filter(s => s.topicId === topicId);
      } else {
        // Use Firebase for real auth
        const result = await getSessionsByTopic(user.uid, topicId);
        if (result.success && result.data) {
          // Normalize dates from Firebase Timestamps
          return result.data.map((s: RawSessionData) => normalizeSessionDates(s));
        }
        return [];
      }
    } catch (err) {
      console.error('[useSessions] Error fetching sessions by topic:', err);
      return [];
    }
  }, [user?.uid, isMockAuth, localStorageKey]);

  // Create a new session
  const createNewSession = useCallback(async (data: CreateSessionInput): Promise<{ success: boolean; id?: string; error?: string }> => {
    if (!user?.uid) {
      return { success: false, error: 'Ingen bruger logget ind' };
    }

    try {
      const sessionData: Omit<SessionData, 'id'> = {
        userId: user.uid,
        topicId: data.topicId,
        topicTitle: data.topicTitle || '',
        subtopicIds: data.subtopicIds || [],
        teachingForm: data.teachingForm,
        learningJourneyId: data.learningJourneyId,
        startedAt: new Date().toISOString(),
        status: 'active',
        goals: data.goals || [],
        confidenceChecks: { positive: 0, needsRepeat: 0, hasQuestion: 0 },
      };

      if (isMockAuth) {
        // Mock auth: use localStorage
        const newSession: SessionData = {
          ...sessionData,
          id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        };

        const existingSessions = JSON.parse(localStorage.getItem(localStorageKey) || '[]');
        existingSessions.unshift(newSession); // Add to beginning (most recent first)
        localStorage.setItem(localStorageKey, JSON.stringify(existingSessions));

        // Update local state
        setSessions(existingSessions);

        return { success: true, id: newSession.id };
      } else {
        // Firebase: use CRUD function
        const result = await createSession(sessionData);

        if (result.success && result.id) {
          // Refresh sessions list
          await fetchSessions();
          return { success: true, id: result.id };
        } else {
          return { success: false, error: result.error || 'Kunne ikke oprette session' };
        }
      }
    } catch (err) {
      console.error('[useSessions] Error creating session:', err);
      return { success: false, error: 'Der opstod en fejl ved oprettelse af session' };
    }
  }, [user?.uid, isMockAuth, localStorageKey, fetchSessions]);

  // Update an existing session
  const updateSessionData = useCallback(async (sessionId: string, data: UpdateSessionInput): Promise<{ success: boolean; error?: string }> => {
    if (!user?.uid) {
      return { success: false, error: 'Ingen bruger logget ind' };
    }

    try {
      if (isMockAuth) {
        // Mock auth: update in localStorage
        const existingSessions: SessionData[] = JSON.parse(localStorage.getItem(localStorageKey) || '[]');
        const index = existingSessions.findIndex((s) => s.id === sessionId);

        if (index === -1) {
          return { success: false, error: 'Session ikke fundet' };
        }

        const updated = {
          ...existingSessions[index],
          ...data,
        };
        existingSessions[index] = updated;
        localStorage.setItem(localStorageKey, JSON.stringify(existingSessions));

        // Update local state
        setSessions(existingSessions);

        return { success: true };
      } else {
        // Firebase: use CRUD function
        const result = await updateSession(sessionId, data);

        if (result.success) {
          // Refresh sessions list
          await fetchSessions();
          return { success: true };
        } else {
          return { success: false, error: result.error || 'Kunne ikke opdatere session' };
        }
      }
    } catch (err) {
      console.error('[useSessions] Error updating session:', err);
      return { success: false, error: 'Der opstod en fejl ved opdatering af session' };
    }
  }, [user?.uid, isMockAuth, localStorageKey, fetchSessions]);

  // End a session (convenience method)
  const endSession = useCallback(async (
    sessionId: string,
    summary?: string,
    keyTakeaways?: string[]
  ): Promise<{ success: boolean; error?: string }> => {
    if (!user?.uid) {
      return { success: false, error: 'Ingen bruger logget ind' };
    }

    try {
      // Find the session to calculate duration
      let sessionStart: Date | undefined;

      if (isMockAuth) {
        const existingSessions: SessionData[] = JSON.parse(localStorage.getItem(localStorageKey) || '[]');
        const session = existingSessions.find(s => s.id === sessionId);
        if (session) {
          sessionStart = new Date(session.startedAt);
        }
      } else {
        const result = await getSession(sessionId);
        if (result.success && result.data) {
          const normalized = normalizeSessionDates(result.data);
          sessionStart = new Date(normalized.startedAt);
        }
      }

      const endedAt = new Date();
      const durationMinutes = sessionStart
        ? Math.round((endedAt.getTime() - sessionStart.getTime()) / 60000)
        : 0;

      const updateData: UpdateSessionInput = {
        endedAt: endedAt.toISOString(),
        durationMinutes,
        status: 'completed',
      };

      if (summary !== undefined) {
        updateData.summary = summary;
      }

      if (keyTakeaways !== undefined) {
        updateData.keyTakeaways = keyTakeaways;
      }

      return updateSessionData(sessionId, updateData);
    } catch (err) {
      console.error('[useSessions] Error ending session:', err);
      return { success: false, error: 'Der opstod en fejl ved afslutning af session' };
    }
  }, [user?.uid, isMockAuth, localStorageKey, updateSessionData]);

  // Delete a session
  const deleteSessionById = useCallback(async (sessionId: string): Promise<{ success: boolean; error?: string }> => {
    if (!user?.uid) {
      return { success: false, error: 'Ingen bruger logget ind' };
    }

    try {
      if (isMockAuth) {
        // Mock auth: remove from localStorage
        const existingSessions: SessionData[] = JSON.parse(localStorage.getItem(localStorageKey) || '[]');
        const filtered = existingSessions.filter((s) => s.id !== sessionId);
        localStorage.setItem(localStorageKey, JSON.stringify(filtered));

        // Update local state
        setSessions(filtered);

        return { success: true };
      } else {
        // Firebase: use CRUD function
        const result = await deleteSession(sessionId);

        if (result.success) {
          // Refresh sessions list
          await fetchSessions();
          return { success: true };
        } else {
          return { success: false, error: result.error || 'Kunne ikke slette session' };
        }
      }
    } catch (err) {
      console.error('[useSessions] Error deleting session:', err);
      return { success: false, error: 'Der opstod en fejl ved sletning af session' };
    }
  }, [user?.uid, isMockAuth, localStorageKey, fetchSessions]);

  // Get a single session by ID (fetches fresh data)
  const getSessionById = useCallback(async (sessionId: string): Promise<SessionData | null> => {
    if (!user?.uid) {
      return null;
    }

    try {
      if (isMockAuth) {
        // Mock auth: get from localStorage
        const existingSessions: SessionData[] = JSON.parse(localStorage.getItem(localStorageKey) || '[]');
        return existingSessions.find((s) => s.id === sessionId) || null;
      } else {
        // Firebase: use CRUD function
        const result = await getSession(sessionId);
        if (result.success && result.data) {
          return normalizeSessionDates(result.data);
        }
        return null;
      }
    } catch (err) {
      console.error('[useSessions] Error getting session:', err);
      return null;
    }
  }, [user?.uid, isMockAuth, localStorageKey]);

  // Get a session from the cached list (no network call)
  const getSessionFromCache = useCallback((sessionId: string): SessionData | undefined => {
    return sessions.find(s => s.id === sessionId);
  }, [sessions]);

  // Get recent sessions (sorted by startedAt descending)
  const getRecentSessions = useCallback((limit: number = 5): SessionData[] => {
    return sessions
      .filter(s => s.status === 'completed')
      .sort((a, b) => {
        const dateA = new Date(a.startedAt).getTime();
        const dateB = new Date(b.startedAt).getTime();
        return dateB - dateA;
      })
      .slice(0, limit);
  }, [sessions]);

  // Get sessions filtered by teaching form
  const getSessionsByForm = useCallback((form: TeachingForm): SessionData[] => {
    return sessions.filter(s => s.teachingForm === form);
  }, [sessions]);

  // Feature #249: Get unfinished/active sessions (not completed or cancelled)
  // Sessions that were interrupted (e.g., due to voice time limit) remain in 'active' status
  const getUnfinishedSessions = useCallback((): SessionData[] => {
    return sessions
      .filter(s => s.status === 'active')
      .sort((a, b) => {
        // Sort by startedAt descending (most recent first)
        const dateA = new Date(a.startedAt).getTime();
        const dateB = new Date(b.startedAt).getTime();
        return dateB - dateA;
      });
  }, [sessions]);

  // Fetch sessions on mount and when user changes
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  return {
    sessions,
    loading,
    error,
    fetchSessions,
    fetchSessionsByTopic,
    createNewSession,
    updateSessionData,
    endSession,
    deleteSessionById,
    getSessionById,
    getSessionFromCache,
    getRecentSessions,
    getSessionsByForm,
    getUnfinishedSessions, // Feature #249
  };
}

export default useSessions;
